import { IArnoSelectorCatalog } from '../../hooks/reactQuerys/ArnoAdminQueries';

/**
 * Der Name, unter dem ein DiNo-Feld in der Oberfläche erscheint (#1245).
 *
 * Der technische Pfad bleibt die Wahrheit — gezeigt wird er nicht. Eine Bedingung, die
 * `LandRegisterSheetArray[index=1].InventoryEntryArray[index=1].CoOwnershipShare` nennt, ist für
 * eine Notarfachangestellte keine Bedingung, sondern eine Fehlermeldung. Gezeigt wird deshalb der
 * Name aus dem Feldkatalog, mitsamt der Person oder dem Blatt, zu dem das Feld gehört:
 * »Erwerber 1 · Anrede und Nachname«.
 *
 * Der Katalog ist dieselbe Quelle, aus der die Feldauswahl und die Selektorprüfung leben; hier
 * entsteht keine zweite Namensliste.
 */

const INDEX = /\[index=\d+\]/g;

/** Wie die Selektorprüfung: jeder Listenindex zählt für die Suche als der erste. */
export const normalisePath = (path: string): string =>
  path.replace(INDEX, '[index=1]');

export type FieldLabelResolver = (path: string) => string | undefined;

/**
 * Auflösung Pfad → Klartextname. Erst der genaue Pfad (der zweite Erwerber heißt anders als der
 * erste), dann der auf den ersten Index normalisierte — sonst bliebe »Grundbuchblatt 2« namenlos,
 * obwohl der Katalog dasselbe Feld kennt.
 */
const CLIENT_BY_INDEX = /^LegalClientArray\[index=(\d+)\]\.(.+)$/;
const CLIENT_BY_ROLE =
  /^LegalClientArray\[LegalRole=([^,\]]+),occurrence=(\d+)\]\.(.+)$/;
const INDEX_NUMBERS = /\[index=(\d+)\]/g;

/**
 * Auflösung Pfad → Klartextname (#1245, Teil 43).
 *
 * Erst der genaue Pfad. Kennt der Katalog ihn nicht — ein anderer Listenindex als im
 * Vorschauvorgang, ein dritter Erwerber, wo er nur zwei hat —, dann der Feldname **ohne** die
 * Person oder das Blatt aus dem Katalog: vorher hieß `LegalClientArray[index=7].LastName` so wie
 * das Feld des ersten Beteiligten, samt dessen Rolle, und Grundbuchblatt 3 hieß »Bestandsverzeichnis
 * 1«. Ein fremder Name an einem Feld ist schlimmer als gar keiner.
 */
export const fieldLabelResolver = (
  catalog: IArnoSelectorCatalog | undefined
): FieldLabelResolver => {
  const byPath = new Map<string, string>();
  /** Feldname ohne Person/Blatt, je normalisiertem Pfad bzw. je Beteiligtenfeld. */
  const bare = new Map<string, string>();
  const clientField = new Map<string, string>();
  (catalog?.groups ?? []).forEach(group =>
    group.fields.forEach(field => {
      // Der Name kommt fertig aus dem Katalog (`selectors.displayName`) — dieselbe Regel wie für
      // die fehlenden Angaben. Der Rest ist die Schreibweise älterer Kataloge.
      const name =
        field.displayName ??
        (field.subgroup ? `${field.subgroup} · ${field.label}` : field.label);
      byPath.set(field.path, name);
      const normal = normalisePath(field.path);
      if (!bare.has(normal)) {
        bare.set(normal, field.label);
      }
      const client = /^LegalClientArray\[[^\]]*\]\.(.+)$/.exec(field.path);
      if (client && !clientField.has(client[1])) {
        clientField.set(client[1], field.label);
      }
    })
  );
  return (path: string) => {
    if (!path) {
      return undefined;
    }
    const exact = byPath.get(path);
    if (exact) {
      return exact;
    }
    const byRole = CLIENT_BY_ROLE.exec(path);
    if (byRole) {
      const label = clientField.get(byRole[3]);
      const occurrence = Number(byRole[2]);
      return label
        ? `${byRole[1]}${occurrence > 1 ? ` ${occurrence}` : ''} · ${label}`
        : undefined;
    }
    const byIndex = CLIENT_BY_INDEX.exec(path);
    if (byIndex) {
      const label = clientField.get(byIndex[2]);
      return label
        ? `Beteiligter an Stelle ${byIndex[1]} · ${label}`
        : undefined;
    }
    const label = bare.get(normalisePath(path));
    if (!label) {
      return undefined;
    }
    const numbers = Array.from(path.matchAll(INDEX_NUMBERS)).map(
      match => match[1]
    );
    return numbers.some(number => number !== '1')
      ? `${label} (Nr. ${numbers.join(' / ')})`
      : label;
  };
};
